/**
 * Marketing Terminal — Dashboard
 *
 * Phase-1 landing page at /admin/marketing. Shows KPI tiles, Kanban preview,
 * and brand kit confirmation. Subsequent phases add: editorial calendar
 * snippet, listening alerts feed, persona allocation, budget burn meter.
 *
 * See:
 *   - docs/plans/marketing-terminal-master-spec.md
 *   - docs/plans/wobbly-pondering-codd.md
 *
 * Backend: GET /api/admin/marketing/dashboard/summary (KPIs)
 *          GET /api/admin/marketing/social/kanban     (Kanban preview)
 *          GET /api/admin/marketing/brand-kit/active  (brand chip)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  Lightbulb,
  Megaphone,
  Palette,
  Send,
  TrendingUp,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';
import { KpiTile } from '../analytics/components/KpiTile';
import { formatInt } from '../analytics/format';

// ── Types ────────────────────────────────────────────────────────────────────

interface DashboardSummary {
  pending_review: number;
  scheduled_this_week: number;
  new_listening_alerts_high_relevance: number;
  new_content_ideas: number;
  publish_failures: number;
}

interface KanbanColumn {
  id: string;
  status: string;
  content_text?: string;
  platforms?: string[];
  scheduled_at?: string;
  primary_persona_id?: string;
  pillar_tags?: string[];
  deployment_strength?: number;
}

interface KanbanResponse {
  data: Record<string, KanbanColumn[]>;
  totals: Record<string, number>;
}

interface BrandKit {
  id: string;
  brand_name: string;
  version_number: number;
  primary_color: string;
  pillars: Array<{ key: string; name: string; description: string }>;
  voice_traits: Array<{ key: string; name: string }>;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

// ── QueryClient ──────────────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: 1 },
  },
});

// ── Public export ────────────────────────────────────────────────────────────

export function MarketingDashboard() {
  return (
    <QueryClientProvider client={queryClient}>
      <DashboardInner />
    </QueryClientProvider>
  );
}

// ── Inner component ──────────────────────────────────────────────────────────

function DashboardInner() {
  const summary = useQuery<Envelope<DashboardSummary>>({
    queryKey: ['marketing-dashboard-summary'],
    queryFn: () => adminFetch<Envelope<DashboardSummary>>('/api/admin/marketing/dashboard/summary'),
    refetchInterval: 60_000,
  });

  const kanban = useQuery<KanbanResponse>({
    queryKey: ['marketing-kanban-preview'],
    queryFn: () => adminFetch<KanbanResponse>('/api/admin/marketing/social/kanban'),
    refetchInterval: 60_000,
  });

  const brand = useQuery<Envelope<BrandKit>>({
    queryKey: ['marketing-brand-kit'],
    queryFn: () => adminFetch<Envelope<BrandKit>>('/api/admin/marketing/brand-kit/active'),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const s = summary.data?.data;
  const cols = kanban.data?.data;
  const totals = kanban.data?.totals;
  const bk = brand.data?.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1
            className="text-2xl font-bold"
            style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}
          >
            Marketing Terminal
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Pipeline · audience · listening · creative · channels · budget. Phase 1.
          </p>
        </div>

        {/* Brand kit chip — visual confirmation that brand voice is loaded */}
        {bk && (
          <Link
            to="/admin/marketing/brand"
            className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-xs hover:bg-gray-50"
            style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}
          >
            <Palette className="h-3 w-3" style={{ color: bk.primary_color }} />
            <span className="font-semibold text-gray-800">
              {bk.brand_name} v{bk.version_number}
            </span>
            <span className="text-gray-400">· {bk.pillars?.length ?? 0} pillars · {bk.voice_traits?.length ?? 0} traits</span>
          </Link>
        )}
        {brand.error && (
          <div className="inline-flex items-center gap-2 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            <span>No active brand kit. Run migration 1383 (brand_kit_v1_seed).</span>
          </div>
        )}
      </div>

      {/* Row 1 — Action KPIs */}
      <section>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Needs Attention
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <KpiTile
            label="Pending Review"
            value={formatInt(s?.pending_review)}
            hint="Drafts ready for your approval"
            icon={<Clock className="h-3 w-3" />}
            loading={summary.isLoading}
          />
          <KpiTile
            label="Scheduled This Week"
            value={formatInt(s?.scheduled_this_week)}
            hint="Approved & queued for publish"
            icon={<Calendar className="h-3 w-3" />}
            loading={summary.isLoading}
          />
          <KpiTile
            label="High-Relevance Alerts"
            value={formatInt(s?.new_listening_alerts_high_relevance)}
            hint="≥0.7 relevance, unreviewed"
            icon={<Activity className="h-3 w-3" />}
            loading={summary.isLoading}
          />
          <KpiTile
            label="New Ideas"
            value={formatInt(s?.new_content_ideas)}
            hint="In the idea pipeline"
            icon={<Lightbulb className="h-3 w-3" />}
            loading={summary.isLoading}
          />
          <KpiTile
            label="Publish Failures"
            value={formatInt(s?.publish_failures)}
            hint="Need re-auth or retry"
            icon={<AlertTriangle className="h-3 w-3" />}
            loading={summary.isLoading}
          />
        </div>
      </section>

      {/* Row 2 — Kanban preview */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Pipeline
          </div>
          <Link
            to="/admin/marketing/social"
            className="text-xs font-medium text-gray-600 hover:text-gray-900"
            style={{ color: 'var(--cedar-primary)' }}
          >
            Open full Kanban →
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-7">
          {[
            { key: 'draft', label: 'Draft', icon: Megaphone },
            { key: 'ready_for_review', label: 'Ready for Review', icon: Clock },
            { key: 'approved', label: 'Approved', icon: CheckCircle2 },
            { key: 'scheduled', label: 'Scheduled', icon: Calendar },
            { key: 'published', label: 'Published', icon: Send },
            { key: 'publish_failed', label: 'Failed', icon: AlertTriangle },
            { key: 'archived', label: 'Archived', icon: Activity },
          ].map(({ key, label, icon: Icon }) => {
            const count = totals?.[key] ?? 0;
            const items = cols?.[key] ?? [];
            const isFailure = key === 'publish_failed' && count > 0;
            const isUrgent = key === 'ready_for_review' && count > 0;

            return (
              <div
                key={key}
                className={`rounded-lg bg-white p-3 ${isFailure ? 'ring-2 ring-red-300' : ''} ${isUrgent ? 'ring-2 ring-amber-300' : ''}`}
                style={{
                  boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)',
                }}
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                      {label}
                    </span>
                  </div>
                  <span
                    className={`text-xs font-bold ${isFailure ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-gray-800'}`}
                  >
                    {count}
                  </span>
                </div>

                {/* Top 2 cards preview */}
                {items.slice(0, 2).map(item => (
                  <Link
                    key={item.id}
                    to={`/admin/marketing/social/${item.id}`}
                    className="mb-1.5 block rounded border border-gray-100 bg-gray-50 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
                  >
                    <div className="line-clamp-2">{item.content_text ?? '(no text)'}</div>
                    {item.platforms && item.platforms.length > 0 && (
                      <div className="mt-1 text-[10px] text-gray-500">
                        {item.platforms.join(' · ')}
                      </div>
                    )}
                  </Link>
                ))}

                {items.length > 2 && (
                  <div className="text-[10px] text-gray-400">
                    +{items.length - 2} more
                  </div>
                )}
                {items.length === 0 && (
                  <div className="text-[10px] italic text-gray-300">empty</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Row 3 — Phase indicator + next-step hints */}
      <section className="rounded-lg bg-white p-4" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
        <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Phase 1 Progress
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />
            <span>
              <strong>Schema in.</strong> marketing.* tables live (migrations 1358-1359).
            </span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />
            <span>
              <strong>Brand kit v1 seeded.</strong> Voice corpus + pillars + forbidden words loaded.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />
            <span>
              <strong>Capability backbone.</strong> requires_capability() decorator + 11 role bundles.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Clock className="mt-0.5 h-4 w-4 text-gray-400" />
            <span className="text-gray-600">
              <strong>Next:</strong> connect LinkedIn + X (Phase 2 publishers).
              Generate cowork_agent API key from Settings → Connections & Keys.
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

export default MarketingDashboard;
